import type { TimerPhase } from 'model';

interface TimerDisplayProps {
  timeRemaining: number;
  phase: TimerPhase;
  currentSet: number;
  totalSets?: number;
}

/**
 * Format seconds into MM:SS or just SS display
 */
function formatTime(seconds: number): string {
  if (seconds < 0) seconds = 0;

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  if (mins > 0) {
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  return secs.toString();
}

/**
 * Get the display label for the current phase
 */
function getPhaseLabel(phase: TimerPhase): string {
  switch (phase) {
    case 'work':
      return 'WORK';
    case 'rest':
      return 'REST';
    case 'complete':
      return 'COMPLETE';
    default:
      return 'READY';
  }
}

/**
 * Get CSS class for phase-based styling
 */
function getPhaseClass(phase: TimerPhase): string {
  switch (phase) {
    case 'work':
      return 'timer-phase-work';
    case 'rest':
      return 'timer-phase-rest';
    case 'complete':
      return 'timer-phase-complete';
    default:
      return 'timer-phase-idle';
  }
}

export function TimerDisplay({ timeRemaining, phase, currentSet, totalSets }: TimerDisplayProps): JSX.Element {
  const isWarning = phase === 'work' && timeRemaining <= 10 && timeRemaining > 0;

  return (
    <div className={`timer-display ${getPhaseClass(phase)} ${isWarning ? 'timer-warning' : ''}`}>
      <div className="timer-phase-label">{getPhaseLabel(phase)}</div>

      <div className="timer-countdown">{formatTime(timeRemaining)}</div>

      {currentSet > 0 && (
        <div className="timer-set-progress">
          Set {currentSet}
          {totalSets && ` / ${totalSets === -1 ? '∞' : totalSets}`}
        </div>
      )}
    </div>
  );
}
